import { ClientLoggingService } from '../shared/client-logging.service';
import { Injectable, inject } from '@angular/core';
import { Subject } from 'rxjs';
import { FirebaseAuthService } from './firebase-auth.service';

/** Forces a Firebase token refresh after permission changes (invite/remove) so updated custom claims take effect immediately. */
@Injectable({
  providedIn: 'root',
})
export class PermissionRefreshService {
  private firebaseAuthService = inject(FirebaseAuthService);

  private permissionChangedSubject = new Subject<{ type: 'invite' | 'remove'; projectId: string; userEmail: string }>();

  // Observable for components to listen to permission changes
  permissionChanged$ = this.permissionChangedSubject.asObservable();

  /**
   * Notify that a user's permissions have changed and force token refresh
   */
  async notifyPermissionChange(type: 'invite' | 'remove', projectId: string, userEmail: string): Promise<void> {
    ClientLoggingService.log(
      'INFO',
      `[PermissionRefreshService] Permission change detected: ${type} ${userEmail} for project ${projectId}`
    );

    try {
      // Force refresh the current user's token to get updated custom claims
      await this.firebaseAuthService.refreshUserToken();

      // Emit event for components to react (clear caches, refresh data, etc.)
      this.permissionChangedSubject.next({ type, projectId, userEmail });

      ClientLoggingService.log('INFO', `[PermissionRefreshService] Permission refresh completed for ${type} operation`);
    } catch (error) {
      ClientLoggingService.log('ERROR', `[PermissionRefreshService] Error during permission refresh:`, error);
      throw error;
    }
  }

  /**
   * Force refresh current user's permissions without specific event details
   */
  async refreshCurrentUserPermissions(): Promise<void> {
    ClientLoggingService.log('INFO', `[PermissionRefreshService] Forcing current user permission refresh...`);

    try {
      await this.firebaseAuthService.refreshUserToken();
      ClientLoggingService.log('INFO', `[PermissionRefreshService] Current user permissions refreshed successfully`);
    } catch (error) {
      ClientLoggingService.log('ERROR', `[PermissionRefreshService] Error refreshing current user permissions:`, error);
      throw error;
    }
  }
}
